import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BreadcrumbShowCountlabel } from '../../store/actions';
import GlobalCustomTable from '../../GlobalCustomTable';
import { ItemSaleContext } from './ContextDataProvider';
import GridExample from './Pivottable';

// Map unit dropdown values to the correct qty data field
const unitValueToFieldMap = {
  1: 'QtyInNo',
  2: 'QtyInKg',
  3: 'QtyInBox',
  4: 'BaseItemUnitQuantity',
};

function ShowTable() {
  const dispatch = useDispatch();

  const {
    tableData,
    selectedColumns,
    pivotMode,
    unitDropdownSelect,
  } = ItemSaleContext();

  const [dataWithTotal, setDataWithTotal] = useState([]);

  const qtyFieldName = unitValueToFieldMap[unitDropdownSelect?.value] || '';

  // Compute grand total row
  useEffect(() => {
    if (tableData && tableData.length > 0 && selectedColumns.length > 0) {
      const totalRow = { id: 'total-row', _isTotalRow: true };

      selectedColumns.forEach((col, idx) => {
        if (idx === 0) {
          totalRow[col.dataField] = 'Grand Total';
        } else {
          const isNumeric = tableData.some(item =>
            !isNaN(parseFloat(item[col.dataField]))
          );

          if (isNumeric) {
            const total = tableData.reduce(
              (acc, item) => acc + (parseFloat(item[col.dataField]) || 0),
              0
            );
            totalRow[col.dataField] = total;
          } else {
            totalRow[col.dataField] = '';
          }
        }
      });

      setDataWithTotal([...tableData, totalRow]);
    } else {
      setDataWithTotal(tableData || []);
    }
  }, [tableData, selectedColumns]);

  // Formatters: Only qty field and amount get custom formatting
  const columnsWithFormatter = selectedColumns.map(col => {
    const isQtyColumn = col.dataField === qtyFieldName;
    const isAmountColumn = col.dataField === 'Amount';

    return {
      ...col,
      formatter: (cell, row) => {
        if (typeof cell === 'number') {
          if (isQtyColumn) {
            // Qty field → format to 2 decimal places (no ₹)
            return row._isTotalRow
              ? <strong>{cell.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              : cell.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }

          if (isAmountColumn) {
            // Amount field → with ₹ symbol
            const value = `₹ ${cell.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            return row._isTotalRow ? <strong>{value}</strong> : value;
          }
        }

        // Fallback for non-number or other columns
        return row._isTotalRow ? <strong>{cell}</strong> : cell;
      },
      footerAlign: 'right',
    };
  });

  // Update breadcrumb info on change
  useEffect(() => {
    const totalRow = dataWithTotal.find(row => row._isTotalRow);
    const filteredRows = dataWithTotal.filter(row => !row._isTotalRow);
    const count = filteredRows.length;

    const amountTotal = totalRow?.Amount || 0;
    const qtyTotal = qtyFieldName && totalRow?.[qtyFieldName] != null
      ? totalRow[qtyFieldName]
      : 0;

    const breadcrumbLabel = `Count: ${count} | ₹${amountTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })} | ${qtyTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits:2 })}`;
    dispatch(BreadcrumbShowCountlabel(breadcrumbLabel));
  }, [dataWithTotal, qtyFieldName, dispatch]);

  if (pivotMode) {
    return <GridExample />;
  }

  return (
    <GlobalCustomTable
      keyField="id"
      data={dataWithTotal}
      columns={columnsWithFormatter}
      paginationEnabled={false}
      rowStyle={row =>
        row._isTotalRow
          ? { fontWeight: 'bold', backgroundColor: '#f2f2f2' }
          : {}
      }
      noDataIndication={
        <div className="text-danger text-center table-cursor-pointer">
          Data Not available
        </div>
      }
    />
  );
}

export default React.memo(ShowTable);
